import { LogMessageType } from "../utils/messages";
import { GAIN_TIME_CONST, SourceType } from "../components/audio-params";

let delayedReconnectTimeout = null;
function performDelayedReconnect(gainNode) {
  if (delayedReconnectTimeout) {
    clearTimeout(delayedReconnectTimeout);
  }

  delayedReconnectTimeout = setTimeout(() => {
    delayedReconnectTimeout = null;
    console.warn(
      "enableChromeAEC: recreate RTCPeerConnection loopback because the local connection was disconnected for 10s"
    );
    // eslint-disable-next-line no-use-before-define
    enableChromeAEC(gainNode);
  }, 10000);
}

import * as sdpTransform from "sdp-transform";
import MediaDevicesManager from "../utils/media-devices-manager";

function isThreeAudio(node) {
  return node instanceof THREE.Audio || node instanceof THREE.PositionalAudio;
}

async function enableChromeAEC(gainNode) {
  /**
   *  workaround for: https://bugs.chromium.org/p/chromium/issues/detail?id=687574
   *  1. grab the GainNode from the scene's THREE.AudioListener
   *  2. disconnect the GainNode from the AudioDestinationNode (basically the audio out), this prevents hearing the audio twice.
   *  3. create a local webrtc connection between two RTCPeerConnections (see this example: https://webrtc.github.io/samples/src/content/peerconnection/pc1/)
   *  4. create a new MediaStreamDestination from the scene's THREE.AudioContext and connect the GainNode to it.
   *  5. add the MediaStreamDestination's track  to one of those RTCPeerConnections
   *  6. connect the other RTCPeerConnection's stream to a new audio element.
   *  All audio is now routed through Chrome's audio mixer, thus enabling AEC, while preserving all the audio processing that was performed via the WebAudio API.
   */

  const audioEl = new Audio();
  audioEl.setAttribute("autoplay", "autoplay");
  audioEl.setAttribute("playsinline", "playsinline");

  const context = THREE.AudioContext.getContext();
  const loopbackDestination = context.createMediaStreamDestination();
  const outboundPeerConnection = new RTCPeerConnection();
  const inboundPeerConnection = new RTCPeerConnection();

  const onError = e => {
    console.error("enableChromeAEC: RTCPeerConnection loopback initialization error", e);
  };

  outboundPeerConnection.addEventListener("icecandidate", e => {
    inboundPeerConnection.addIceCandidate(e.candidate).catch(onError);
  });
  outboundPeerConnection.addEventListener("iceconnectionstatechange", () => {
    console.warn(
      "enableChromeAEC: outboundPeerConnection state changed to " + outboundPeerConnection.iceConnectionState
    );
    if (outboundPeerConnection.iceConnectionState === "disconnected") {
      performDelayedReconnect(gainNode);
    }
    if (outboundPeerConnection.iceConnectionState === "connected") {
      if (delayedReconnectTimeout) {
        // The RTCPeerConnection reconnected by itself, cancel recreating the
        // local connection.
        clearTimeout(delayedReconnectTimeout);
      }
    }
  });

  inboundPeerConnection.addEventListener("icecandidate", e => {
    outboundPeerConnection.addIceCandidate(e.candidate).catch(onError);
  });
  inboundPeerConnection.addEventListener("iceconnectionstatechange", () => {
    console.warn("enableChromeAEC: inboundPeerConnection state changed to " + inboundPeerConnection.iceConnectionState);
    if (inboundPeerConnection.iceConnectionState === "disconnected") {
      performDelayedReconnect(gainNode);
    }
    if (inboundPeerConnection.iceConnectionState === "connected") {
      if (delayedReconnectTimeout) {
        // The RTCPeerConnection reconnected by itself, cancel recreating the
        // local connection.
        clearTimeout(delayedReconnectTimeout);
      }
    }
  });

  inboundPeerConnection.addEventListener("track", e => {
    audioEl.srcObject = e.streams[0];
  });

  try {
    //The following should never fail, but just in case, we won't disconnect/reconnect the gainNode unless all of this succeeds
    loopbackDestination.stream.getTracks().forEach(track => {
      outboundPeerConnection.addTrack(track, loopbackDestination.stream);
    });

    const offer = await outboundPeerConnection.createOffer();
    outboundPeerConnection.setLocalDescription(offer);
    await inboundPeerConnection.setRemoteDescription(offer);

    const answer = await inboundPeerConnection.createAnswer();

    // Rewrite SDP to be stereo and (variable) max bitrate
    const parsedSdp = sdpTransform.parse(answer.sdp);
    for (let i = 0; i < parsedSdp.media.length; i++) {
      for (let j = 0; j < parsedSdp.media[i].fmtp.length; j++) {
        parsedSdp.media[i].fmtp[j].config += `;stereo=1;cbr=0;maxaveragebitrate=510000;`;
      }
    }
    answer.sdp = sdpTransform.write(parsedSdp);

    inboundPeerConnection.setLocalDescription(answer);
    outboundPeerConnection.setRemoteDescription(answer);

    gainNode.disconnect();
    gainNode.connect(loopbackDestination);
  } catch (e) {
    onError(e);
  }
}

export class AudioSystem {
  constructor(sceneEl) {
    this._sceneEl = sceneEl;

    this.audioContext = THREE.AudioContext.getContext();
    this.audioNodes = new Map();
    this.mediaStreamDestinationNode = this.audioContext.createMediaStreamDestination(); // Voice, camera, screenshare
    this.audioDestination = null; // Assigned only after an alternate output is ready.
    this.outboundStream = this.mediaStreamDestinationNode.stream;
    this.outboundGainNode = this.audioContext.createGain();
    this.outboundAnalyser = this.audioContext.createAnalyser();
    this.outboundAnalyser.fftSize = 32;
    this.analyserLevels = new Uint8Array(this.outboundAnalyser.fftSize);
    this.outboundGainNode.connect(this.outboundAnalyser);
    this.outboundAnalyser.connect(this.mediaStreamDestinationNode);
    this.audioContextNeedsToBeResumed = false;

    this.mediaGain = this.audioContext.createGain();
    this.mixer = {
      [SourceType.AVATAR_AUDIO_SOURCE]: this.audioContext.createGain(),
      [SourceType.MEDIA_VIDEO]: this.mediaGain,
      [SourceType.AUDIO_TARGET]: this.mediaGain,
      [SourceType.SFX]: this.audioContext.createGain()
    };
    this.mixer[SourceType.AVATAR_AUDIO_SOURCE].connect(this._sceneEl.audioListener.getInput());
    this.mixer[SourceType.MEDIA_VIDEO].connect(this._sceneEl.audioListener.getInput());
    this.mixer[SourceType.SFX].connect(this._sceneEl.audioListener.getInput());

    // Analyser to show the output audio level
    this.mixerAnalyser = this.audioContext.createAnalyser();
    this.mixerAnalyser.fftSize = 32;
    this.mixer[SourceType.AVATAR_AUDIO_SOURCE].connect(this.mixerAnalyser);
    this.mixer[SourceType.MEDIA_VIDEO].connect(this.mixerAnalyser);
    this.mixer[SourceType.SFX].connect(this.mixerAnalyser);

    // Webkit Mobile fix
    this._safariMobileAudioInterruptionFix();

    document.body.addEventListener("touchend", this._resumeAudioContext, false);
    document.body.addEventListener("mouseup", this._resumeAudioContext, false);

    this.onPrefsUpdated = this.updatePrefs.bind(this);
    window.APP.store.addEventListener("statechanged", this.onPrefsUpdated);
  }

  addStreamToOutboundAudio(id, mediaStream) {
    if (this.audioNodes.has(id)) {
      this.removeStreamFromOutboundAudio(id);
    }

    const sourceNode = this.audioContext.createMediaStreamSource(mediaStream);
    const gainNode = this.audioContext.createGain();
    sourceNode.connect(gainNode);
    gainNode.connect(this.outboundGainNode);
    this.audioNodes.set(id, { sourceNode, gainNode });
  }

  removeStreamFromOutboundAudio(id) {
    if (this.audioNodes.has(id)) {
      const nodes = this.audioNodes.get(id);
      nodes.sourceNode.disconnect();
      nodes.gainNode.disconnect();
      this.audioNodes.delete(id);
    }
  }

  addAudio({ sourceType, node }) {
    let outputNode = node;
    if (isThreeAudio(node)) {
      node.gain.disconnect();
      outputNode = node.gain;
    }
    outputNode.connect(this.mixer[sourceType]);
  }

  removeAudio({ node }) {
    let outputNode = node;
    if (isThreeAudio(node)) {
      outputNode = node.gain;
    }
    outputNode.disconnect();
  }

  updatePrefs() {
    const { globalVoiceVolume, globalMediaVolume, globalSFXVolume } = window.APP.store.state.preferences;
    let newGain = globalMediaVolume / 100;
    this.mixer[SourceType.MEDIA_VIDEO].gain.setTargetAtTime(newGain, this.audioContext.currentTime, GAIN_TIME_CONST);

    newGain = globalSFXVolume / 100;
    this.mixer[SourceType.SFX].gain.setTargetAtTime(newGain, this.audioContext.currentTime, GAIN_TIME_CONST);

    newGain = globalVoiceVolume / 100;
    this.mixer[SourceType.AVATAR_AUDIO_SOURCE].gain.setTargetAtTime(
      newGain,
      this.audioContext.currentTime,
      GAIN_TIME_CONST
    );

    if (MediaDevicesManager.isAudioOutputSelectEnabled && APP.mediaDevicesManager) {
      const preference = window.APP.store.state.preferences.preferredSpeakers;
      const manager = APP.mediaDevicesManager;
      const sinkId = manager.selectedSpeakersDeviceId;
      if (this._lastSpeakerPreference === preference && this._lastResolvedSpeaker === sinkId) return;
      this._lastSpeakerPreference = preference;
      this._lastResolvedSpeaker = sinkId;
      const effectiveSinkId = this.outputMediaAudio?.sinkId || manager.defaultOutputDeviceId;
      if (sinkId !== effectiveSinkId || manager.isAudioOutputSwitchPending) return manager.changeAudioOutput(sinkId);
    }
  }

  async setAudioOutput(sinkId, defaultSinkId) {
    const generation = (this._outputSwitchGeneration || 0) + 1;
    this._outputSwitchGeneration = generation;
    // A volume update while preparing a candidate must not request the old preference again.
    this._lastSpeakerPreference = window.APP.store.state.preferences.preferredSpeakers;
    this._lastResolvedSpeaker = APP.mediaDevicesManager.selectedSpeakersDeviceId;
    const isDefault = sinkId === defaultSinkId;
    if ((!this.outputMediaAudio && isDefault) || sinkId === this.outputMediaAudio?.sinkId) return true;

    let candidate = null;
    let destination = null;
    try {
      if (!isDefault) {
        candidate = new Audio();
        if (typeof candidate.setSinkId !== "function") {
          throw Object.assign(new Error("Audio output selection is not supported"), { name: "NotSupportedError" });
        }
        // Prepare an isolated, initially silent stream. Never move the live mixers into
        // an element until both sink selection and unmuted playback have succeeded.
        destination = this.audioContext.createMediaStreamDestination();
        candidate.srcObject = destination.stream;
        await candidate.setSinkId(sinkId);
        if (generation !== this._outputSwitchGeneration) return false;
        await candidate.play();
        if (generation !== this._outputSwitchGeneration) return false;
      }

      const sink = isDefault ? this._sceneEl.audioListener.getInput() : destination;
      const previous = this.outputMediaAudio;
      for (const sourceType of [SourceType.AVATAR_AUDIO_SOURCE, SourceType.MEDIA_VIDEO, SourceType.SFX]) {
        this.mixer[sourceType].disconnect();
        this.mixer[sourceType].connect(sink);
        this.mixer[sourceType].connect(this.mixerAnalyser);
      }
      this.outputMediaAudio = candidate;
      this.audioDestination = destination;
      if (previous) {
        previous.pause();
        previous.srcObject?.getTracks().forEach(track => track.stop());
        previous.srcObject = null;
      }
      return true;
    } finally {
      if (candidate && this.outputMediaAudio !== candidate) {
        candidate.pause();
        candidate.srcObject = null;
        destination?.stream.getTracks().forEach(track => track.stop());
      }
    }
  }

  /**
   * Chrome and Safari will start Audio contexts in a "suspended" state.
   * A user interaction (touch/mouse event) is needed in order to resume the AudioContext.
   */
  _resumeAudioContext = () => {
    this.audioContext.resume();

    setTimeout(() => {
      if (this.audioContext.state === "running") {
        const disableAEC = window.APP.store.state.preferences.disableEchoCancellation;
        if (!AFRAME.utils.device.isMobile() && /chrome/i.test(navigator.userAgent) && !disableAEC) {
          enableChromeAEC(this._sceneEl.audioListener.gain);
        }

        document.body.removeEventListener("touchend", this._resumeAudioContext, false);
        document.body.removeEventListener("mouseup", this._resumeAudioContext, false);
      }
    }, 0);
  };

  // Webkit mobile fix
  // https://stackoverflow.com/questions/10232908/is-there-a-way-to-detect-a-mobile-safari-audio-interruption-headphones-unplugg
  _safariMobileAudioInterruptionFix() {
    this.audioContext.onstatechange = () => {
      if (this.audioContext.state === "suspended") {
        // When you unplug the headphone or when the bluetooth headset disconnects on
        // iOS Safari or Chrome, the state changes to suspended.
        // Chrome Android doesn't go in suspended state for this case.
        document.getElementById("avatar-rig").messageDispatch.log(LogMessageType.audioSuspended);
        document.body.addEventListener("touchend", this._resumeAudioContext, false);
        document.body.addEventListener("mouseup", this._resumeAudioContext, false);
        this.audioContextNeedsToBeResumed = true;
      } else if (this.audioContext.state === "running" && this.audioContextNeedsToBeResumed) {
        this.audioContextNeedsToBeResumed = false;
        document.getElementById("avatar-rig").messageDispatch.log(LogMessageType.audioResumed);
      }
    };
  }
}
